import { C } from '../components/ui'

/* ════════════════════════════════════════════════════════════════════════════
 *  INDUSTRIES — DevinStratus vertical-specific solution catalogue
 *  ────────────────────────────────────────────────────────────────────────────
 *  6 industries × 3 sub-solutions = 18 industry deliverables.
 *
 *  Per-industry (category) shape:
 *    slug, heading, icon, color, bg
 *    desc         — one-line for header mega-menu
 *    tagline      — short H1 for category page hero
 *    overview     — 2–3 sentence industry intro
 *    industryStats — 4 sector-relevant credibility numbers
 *    regulations  — array of compliance frameworks we operate under
 *    animVariant  — which CategoryHeroAnimation variant to use
 *    animAccent   — animation accent colour
 *    items[]      — 3 sub-items (deliverables)
 *
 *  Per-item shape (mirrors SOLUTIONS schema, with industry-specific additions):
 *    slug, n, t, tag?, d
 *    hero               — 60–80 word value proposition
 *    challenges         — 4 industry pain points this addresses
 *    approach           — 4 capability blocks { n, t, d }
 *    features           — 6–8 deliverables
 *    microsoftStack     — 4–5 MS products powering this
 *    benefits           — 4 metric tiles { v, u, l }
 *    outcomes           — concrete client result sentence
 *    relatedSolutions   — array of SOLUTIONS slugs for cross-linking
 *    process            — 5 delivery steps
 *    faq                — 3 Q&A items
 * ════════════════════════════════════════════════════════════════════════════ */

export const INDUSTRIES = [

  /* ════════════ 1. MANUFACTURING ════════════ */
  {
    slug:"manufacturing", heading:"Manufacturing", icon:"Settings", color:C.purple, bg:C.purpleL,
    desc:"Production, quality & shop-floor ops",
    tagline:"Digitise the factory floor — from production to quality to insights",
    overview:"Manufacturers across automotive, aerospace, FMCG, and discrete production trust us to modernise shop-floor operations, integrate legacy MES/SCADA estates with Dynamics 365, and surface real-time insights for production leaders. ISO 9001, IATF 16949, and FDA Part 11 compliance — built in.",
    industryStats:[
      {v:"40+",   l:"Manufacturers served"},
      {v:"£8M",   l:"Average annual savings"},
      {v:"ISO 9001", l:"Compliance-ready"},
      {v:"12 wk",  l:"Average time to live"},
    ],
    regulations:["ISO 9001", "IATF 16949", "FDA 21 CFR Part 11", "GMP"],
    animVariant:"automation",
    animAccent:"#a5b4fc",
    items:[
      {
        slug:"production-automation", n:"Zap", t:"Production Automation", tag:"Most Popular",
        d:"Automate shop-floor processes & systems",
        hero:"Eliminate manual production handoffs, bridge MES/SCADA with ERP, and orchestrate operations end-to-end. We integrate Power Platform with Wonderware, Rockwell, and legacy systems — turning paper-based workflows into digital, auditable, real-time processes. Operators get mobile work orders; supervisors get live KPIs; auditors get verified compliance trails.",
        challenges:[
          "Manual handoffs between MES, ERP and shop floor cost hours per day",
          "Paper-based work orders and quality checks slow production cycles",
          "Compliance audit prep takes weeks of digging through binders",
          "No real-time visibility of OEE, throughput, or downtime causes",
        ],
        approach:[
          { n:"Layers",    t:"MES/ERP orchestration",    d:"Bridge Wonderware, Rockwell, SAP, D365 SCM via Azure Logic Apps and Service Bus — eliminating swivel-chair operations." },
          { n:"Cpu",       t:"Mobile work orders",         d:"Operators receive, complete, and sign off work orders on tablets — with photo capture, time tracking, and parts consumption." },
          { n:"Shield",    t:"Audit-ready trails",         d:"Every action logged with timestamp, operator, and source — ready for ISO 9001, IATF, FDA Part 11 audits." },
          { n:"Chart",     t:"Live KPI dashboards",        d:"OEE, throughput, downtime, scrap rate — surfaced on shop-floor displays and supervisor mobile apps in real time." },
        ],
        features:[
          "MES/SCADA integration (Wonderware, Rockwell, Siemens)",
          "Mobile work order management with offline capability",
          "Quality gate enforcement at each station",
          "Production scheduling integrated with D365 SCM",
          "Real-time OEE and throughput dashboards",
          "Compliance audit-trail automation",
          "Operator training and certification tracking",
          "Predictive downtime alerts via Azure IoT",
        ],
        microsoftStack:["Power Automate","Power Apps","Azure IoT Hub","Dynamics 365 SCM","Power BI"],
        benefits:[
          {v:"34%",u:"",l:"Less unplanned downtime"},
          {v:"22%",u:"",l:"Higher OEE achieved"},
          {v:"100%",u:"",l:"Audit trail coverage"},
          {v:"<12 mo",u:"",l:"Payback period"},
        ],
        outcomes:"Auto-parts manufacturer cut unplanned downtime by 34% and improved OEE from 62% to 76% — recovering £680k/year in production capacity.",
        relatedSolutions:["workflow-automation","operations-management","enterprise-power-apps"],
        process:[
          "Shop-floor process audit & priority workshop (Weeks 1–2)",
          "Integration architecture (MES, SCADA, ERP) (Weeks 3–4)",
          "Build & integrate workflows (Weeks 5–9)",
          "Pilot on one production line (Weeks 10–11)",
          "Plant-wide rollout & operator training (Weeks 12–14)",
        ],
        faq:[
          {q:"Will this work with our existing MES?",a:"Yes — we integrate with all major MES platforms (Wonderware, Rockwell FactoryTalk, Siemens Opcenter) via APIs or middleware. The orchestration layer sits on top; your MES remains the system of record for production execution."},
          {q:"How do we handle multi-plant rollouts?",a:"We pilot on one plant first to validate the design, then template the deployment for additional plants. Multi-plant rollouts typically take 16–24 weeks for 3–5 sites with localisation for language, regulations, and plant-specific processes."},
          {q:"Does this meet IATF 16949 / FDA Part 11 audit requirements?",a:"Yes — we design for traceability and electronic records compliance from day one. Every action is logged with timestamp, user identity, and before/after state. Reports map directly to IATF, ISO 9001, and FDA Part 11 audit requirements."},
        ],
      },
      {
        slug:"quality-operations", n:"Shield", t:"Quality Operations",
        d:"Digital QMS, NCR & compliance",
        hero:"Replace quality binders, Excel logs, and paper NCRs with a fully digital quality management system. Non-conformance routing, supplier corrective actions, calibration records, audit prep — all unified, all searchable, all audit-ready. Built on Power Platform and Dynamics 365 Quality, deployed on your Microsoft tenant.",
        challenges:[
          "Quality records scattered across Excel, paper, and standalone QMS systems",
          "NCR resolution takes weeks because routing is manual",
          "Supplier corrective actions get lost in email threads",
          "Audit prep means digging through binders for 2–3 weeks",
        ],
        approach:[
          { n:"FileText",  t:"Digital NCR management",  d:"Capture non-conformances on the floor, route to QA, track corrective actions through to closure with full evidence." },
          { n:"Users",     t:"Supplier corrective actions",d:"SCAR workflows with portal access for suppliers — submit, review, approve, close with audit trail." },
          { n:"Calc",      t:"Calibration & equipment",   d:"Equipment registers, calibration schedules, certificates of conformity — automated reminders and renewals." },
          { n:"Shield",    t:"Audit-ready evidence",      d:"All quality records linked to part, batch, supplier, work order — one-click audit pack generation." },
        ],
        features:[
          "Mobile NCR capture with photo evidence",
          "Automated NCR routing by severity and product",
          "Supplier portal for corrective action management",
          "Calibration records with auto-renewal alerts",
          "Document control (procedures, work instructions)",
          "Training records linked to certifications",
          "Audit pack generator with one-click PDF export",
          "Statistical process control (SPC) dashboards",
        ],
        microsoftStack:["Power Apps","Power Automate","SharePoint Syntex","Dynamics 365 Quality","Power BI"],
        benefits:[
          {v:"-65%",u:"",l:"NCR resolution time"},
          {v:"100%",u:"",l:"Quality records digital"},
          {v:"1-click",u:"",l:"Audit pack export"},
          {v:"Real-time",u:"",l:"SPC visibility"},
        ],
        outcomes:"Aerospace tier-1 supplier cut NCR cycle time from 18 days to 6 days and passed AS9100 audit with zero findings — saving £340k in audit prep costs annually.",
        relatedSolutions:["approval-automation","ai-document-intelligence","document-management"],
        process:[
          "Quality process audit & system inventory (Weeks 1–2)",
          "NCR & SCAR workflow design (Weeks 3–4)",
          "Build & migrate from legacy QMS (Weeks 5–8)",
          "User pilot with QA team (Weeks 9–10)",
          "Org-wide rollout & supplier onboarding (Weeks 11–12)",
        ],
        faq:[
          {q:"How does this compare to dedicated QMS like Qualio or MasterControl?",a:"For Microsoft-centric manufacturers, we deliver equivalent QMS capability at significantly lower licensing cost. For highly regulated industries (medical devices, pharma), dedicated platforms still have edge — we'll be honest about which fits."},
          {q:"Can suppliers access it without buying licences?",a:"Yes — we use Power Pages for external supplier portals. Suppliers authenticate, submit SCARs, upload evidence, and track resolution without consuming internal M365 licences."},
          {q:"What about statistical process control (SPC) and control charts?",a:"SPC dashboards are built in Power BI with X-bar R charts, capability indices (Cp, Cpk), and out-of-control alerts. Operators get real-time charts on shop-floor displays; managers get aggregated SPC across plants."},
        ],
      },
      {
        slug:"shop-floor-insights", n:"Chart", t:"Shop Floor Insights",
        d:"Real-time visibility for operators & supervisors",
        hero:"Operators walking the floor with clipboards is over. Live dashboards on shop-floor displays, tablets, and supervisor phones show OEE, throughput, downtime causes, energy use, and quality metrics in real time. Built on Power BI, Azure IoT, and Microsoft Fabric. From IoT sensor data to actionable insight in seconds.",
        challenges:[
          "Status meetings rely on yesterday's data from Excel",
          "Operators can't see how their shift is performing live",
          "Maintenance reacts to breakdowns instead of preventing them",
          "Energy and material waste is invisible until invoices arrive",
        ],
        approach:[
          { n:"Globe",     t:"IoT + ERP unified",         d:"Combine machine telemetry, ERP data, and operator inputs into one unified operational view via Azure IoT Hub." },
          { n:"Chart",     t:"Andon-style dashboards",     d:"Real-time KPI screens on the shop floor — OEE, throughput, downtime, quality. Visible to every operator." },
          { n:"Brain",     t:"Predictive alerts",          d:"ML models predict equipment failures, demand spikes, and quality drift — Field Service auto-generates preventative work." },
          { n:"Cpu",       t:"Mobile supervisor app",      d:"Supervisors get live performance, escalations, and action items on phone — no need to walk the floor for data." },
        ],
        features:[
          "Real-time OEE dashboards (availability × performance × quality)",
          "Downtime root-cause analysis with Pareto charts",
          "Energy & material consumption tracking",
          "Predictive maintenance alerts via Azure ML",
          "Andon-style shop-floor displays",
          "Mobile supervisor app for floor walks",
          "Historical analytics for continuous improvement",
          "Integration with MES, SCADA, ERP, IoT sensors",
        ],
        microsoftStack:["Microsoft Fabric","Azure IoT Hub","Power BI","Azure Machine Learning","Power Apps"],
        benefits:[
          {v:"Real-time",u:"",l:"Shop-floor visibility"},
          {v:"-28%",u:"",l:"Reactive maintenance"},
          {v:"+18%",u:"",l:"Throughput improvement"},
          {v:"<5s",u:"",l:"Dashboard refresh"},
        ],
        outcomes:"FMCG client deployed live OEE dashboards across 4 plants — increased throughput 18% in 6 months and cut emergency maintenance calls by 28%.",
        relatedSolutions:["microsoft-fabric","executive-dashboards","operations-management"],
        process:[
          "KPI workshop with production leaders (Weeks 1–2)",
          "Architecture: IoT, data lake, semantic model (Weeks 3–4)",
          "Sensor integration & data ingestion (Weeks 5–8)",
          "Dashboards & predictive models build (Weeks 9–11)",
          "Pilot site → multi-plant rollout (Weeks 12–14)",
        ],
        faq:[
          {q:"What if our equipment is old and has no sensors?",a:"We retrofit IoT sensors (vibration, current, temperature) connected to Azure IoT Edge gateways. Most legacy machines can be sensorised for £5k–£15k per machine, with payback under 12 months."},
          {q:"How real-time is real-time?",a:"For most production KPIs: 5–15 seconds end-to-end (sensor → cloud → dashboard). For machine-state changes triggering Andon alerts: <2 seconds. We design the pipeline based on your latency requirements."},
          {q:"Can it integrate with existing OEE software like LineView or Faciliworks?",a:"Yes — we frequently overlay our solution on top of existing OEE systems, ingesting their data into Fabric for advanced analytics, predictions, and unified cross-plant views without replacing what works."},
        ],
      },
    ],
  },


  /* ════════════ 2. HEALTHCARE ════════════ */
  {
    slug:"healthcare", heading:"Healthcare", icon:"Heart", color:C.teal, bg:C.tealL,
    desc:"Patient workflows, data & operations",
    tagline:"Patient-centric operations — secure, compliant, AI-powered",
    overview:"From NHS trusts and US health systems to private clinics, we modernise patient workflows, integrate Epic/Cerner with Microsoft 365, and deploy AI assistants that respect HIPAA and GDPR. Clinical teams spend less time on admin; patients get faster care; operations leaders see real-time performance.",
    industryStats:[
      {v:"18",       l:"Health systems served"},
      {v:"75k+",     l:"Clinicians supported"},
      {v:"HIPAA",    l:"Compliant by design"},
      {v:"Epic/Cerner", l:"Integration certified"},
    ],
    regulations:["HIPAA", "GDPR", "HITRUST", "CQC", "NHS DSP Toolkit"],
    animVariant:"data",
    animAccent:"#67e8f9",
    items:[
      {
        slug:"patient-workflow-automation", n:"Users", t:"Patient Workflow Automation", tag:"Most Popular",
        d:"Streamline care coordination & scheduling",
        hero:"Replace fax-based referrals, manual admission paperwork, and email-driven discharge coordination with intelligent, HIPAA-compliant workflows. Referral routing, appointment automation, admission/discharge orchestration, and care-pathway tracking — all integrated with Epic, Cerner, or your existing EHR. Clinicians spend more time with patients, less time on admin.",
        challenges:[
          "Faxed referrals lose days in transit; nothing tracks completion",
          "Manual admission paperwork delays bed allocation by hours",
          "Discharge coordination across pharmacy, transport, follow-up is email-driven and fragile",
          "Care-pathway adherence is unmeasurable without integrated data",
        ],
        approach:[
          { n:"Layers",    t:"EHR integration",          d:"Bridge Epic, Cerner, NHS systems via HL7 FHIR APIs — no rip-and-replace of clinical systems." },
          { n:"Zap",       t:"Workflow orchestration",     d:"Referral, admission, discharge, transfer workflows with intelligent routing and SLA tracking." },
          { n:"Shield",    t:"HIPAA-grade security",       d:"PHI encryption, access logging, audit trails — meets HIPAA, GDPR, HITRUST, NHS DSP requirements." },
          { n:"Chart",     t:"Pathway analytics",          d:"Live dashboards on length-of-stay, pathway compliance, throughput — by service, ward, clinician." },
        ],
        features:[
          "HL7 FHIR integration with Epic, Cerner, NHS systems",
          "Referral routing with completion tracking",
          "Admission/discharge orchestration workflows",
          "Bed-management dashboards with real-time status",
          "Care-pathway compliance monitoring",
          "Patient communication automation (SMS, email)",
          "Audit trails for HIPAA / NHS DSP compliance",
          "Clinical KPI dashboards (LOS, readmission, satisfaction)",
        ],
        microsoftStack:["Power Automate","Power Apps","Microsoft Cloud for Healthcare","Azure API for FHIR","Power BI"],
        benefits:[
          {v:"-42%",u:"",l:"Referral processing time"},
          {v:"-2.3 day",u:"",l:"Length of stay reduction"},
          {v:"HIPAA",u:"",l:"Audit-ready trails"},
          {v:"24/7",u:"",l:"Always-on workflow"},
        ],
        outcomes:"NHS acute trust reduced average length-of-stay by 2.3 days through automated discharge coordination — freeing 5,400 bed-days/year for emergency capacity.",
        relatedSolutions:["workflow-automation","approval-automation","customer-experience-apps"],
        process:[
          "Clinical workflow discovery (Weeks 1–3)",
          "EHR integration architecture & security review (Weeks 4–5)",
          "FHIR integration & workflow build (Weeks 6–10)",
          "Clinical pilot with one service (Weeks 11–12)",
          "Trust-wide rollout & training (Weeks 13–16)",
        ],
        faq:[
          {q:"Will this work with our existing EHR (Epic, Cerner, EMIS, SystmOne)?",a:"Yes — we integrate via HL7 FHIR APIs (modern standard) or HL7v2 for older systems. EHR remains the clinical system of record; our workflows orchestrate around it without disrupting clinician workflows."},
          {q:"How is PHI protected?",a:"PHI never leaves your Azure tenant. We deploy in your subscription with private endpoints, customer-managed keys, and Microsoft Cloud for Healthcare baseline security. Every PHI access is logged and audited — meeting HIPAA, GDPR, NHS DSP Toolkit, and HITRUST requirements."},
          {q:"Can we start small and expand?",a:"Yes — most clients start with one service line (e.g., orthopaedic discharges) to prove ROI in 8–10 weeks, then expand. The underlying integration platform supports unlimited workflows once established."},
        ],
      },
      {
        slug:"healthcare-data-management", n:"FileText", t:"Healthcare Data Management",
        d:"HIPAA-compliant data platforms",
        hero:"Healthcare data lives in EHRs, lab systems, billing, scheduling, imaging — and rarely speaks to itself. We build unified clinical data platforms on Microsoft Fabric and Azure Health Data Services, with HIPAA-compliant governance and FHIR-native interoperability. The foundation for population health analytics, AI-assisted clinical decision support, and real-world evidence.",
        challenges:[
          "Patient data fragmented across EHR, lab, imaging, billing systems",
          "Population health analytics impossible without unified data",
          "AI initiatives stall because data isn't AI-ready or HIPAA-governed",
          "Regulatory reporting requires manual data assembly from 5+ sources",
        ],
        approach:[
          { n:"Layers",    t:"FHIR-native lakehouse",     d:"Azure Health Data Services as foundation — FHIR-compliant from ingest to consumption, no proprietary lock-in." },
          { n:"Shield",    t:"HIPAA governance",           d:"PHI classification, access policies, audit trails via Microsoft Purview — every access logged, every flow audited." },
          { n:"Brain",     t:"AI-ready architecture",      d:"Clean, labelled, governed data ready for clinical AI workloads — Foundry, Copilot, custom models." },
          { n:"Chart",     t:"Population analytics",       d:"Cross-system analytics: risk stratification, care gap analysis, quality measure reporting." },
        ],
        features:[
          "Azure Health Data Services / FHIR foundation",
          "Multi-source ingestion (EHR, lab, imaging, billing)",
          "PHI classification & sensitivity labelling",
          "HIPAA-grade audit logging and access controls",
          "Population health analytics dashboards",
          "AI-ready data pipelines for clinical ML",
          "Regulatory reporting automation (CMS, HEDIS, CQC)",
          "Real-world evidence (RWE) data marts",
        ],
        microsoftStack:["Microsoft Fabric","Azure Health Data Services","Microsoft Purview","Azure OpenAI","Power BI"],
        benefits:[
          {v:"FHIR",u:"-native",l:"No vendor lock-in"},
          {v:"PHI",u:"-safe",l:"HIPAA classified"},
          {v:"AI",u:"-ready",l:"For Copilot & ML"},
          {v:"Unified",u:"",l:"All data sources"},
        ],
        outcomes:"Regional health system consolidated 9 data sources onto Fabric + AHDS — enabling real-time population health analytics that identified £4.2M in avoidable readmissions.",
        relatedSolutions:["microsoft-fabric","unified-data-platforms","ai-ready-data"],
        process:[
          "Data estate audit & PHI classification (Weeks 1–3)",
          "Architecture design (FHIR, governance) (Weeks 4–5)",
          "Ingestion pipelines & FHIR conversion (Weeks 6–10)",
          "Population health analytics build (Weeks 11–14)",
          "Governance, training, regulatory reporting setup (Weeks 15–16)",
        ],
        faq:[
          {q:"Is this a replacement for our EHR?",a:"No — your EHR remains the clinical system of record. Healthcare data management is the analytical and governance layer on top. We bring data into a unified platform for analytics, AI, and reporting without disturbing clinical workflows."},
          {q:"How do you handle BAAs and HIPAA compliance?",a:"Microsoft signs BAAs (Business Associate Agreements) for Fabric, Azure Health Data Services, and all touched services. We architect for HIPAA from day one and provide compliance documentation for your auditors."},
          {q:"Can it handle imaging data and genomics?",a:"Yes — Azure Health Data Services supports DICOM (imaging) and MedTech (devices). Fabric handles structured, semi-structured, and unstructured data including genomics — though specialist genomics platforms may be better for primary analysis."},
        ],
      },
      {
        slug:"healthcare-operations-portals", n:"Globe", t:"Internal Operations Portals",
        d:"Staff & department service portals",
        hero:"Healthcare staff waste hours navigating between IT helpdesk, HR self-service, rota systems, and policy libraries. We build unified staff experience portals — branded, mobile-friendly, AI-searchable — that bring everything a clinician, nurse, or admin needs into one front door. Built on SharePoint + Power Apps + Microsoft Viva.",
        challenges:[
          "Staff lose hours navigating fragmented internal systems",
          "Policy documents are unfindable; clinicians work from memory",
          "Rota changes, leave requests, IT tickets each require different logins",
          "New starter onboarding takes weeks because nothing is unified",
        ],
        approach:[
          { n:"Globe",     t:"Single front door",        d:"Branded portal — HR, IT, rota, policies, learning — accessible from anywhere with SSO." },
          { n:"Brain",     t:"AI policy search",          d:"Ask 'what's the protocol for X' in natural language; portal cites the right policy, version, and date." },
          { n:"Users",     t:"Personalised content",       d:"Each clinician sees their rota, training due, payslip, leave balance — tailored by role and department." },
          { n:"Shield",    t:"Audit & compliance",         d:"Every access logged, audit-ready, GDPR-compliant — passes NHS DSP Toolkit and HITRUST review." },
        ],
        features:[
          "Branded staff experience portal homepage",
          "Self-service rota and shift swaps",
          "AI-powered policy and protocol search",
          "IT ticketing integration (ServiceNow, Cherwell)",
          "Mandatory training tracking with auto-reminders",
          "Leave, expenses, and HR self-service",
          "Internal news and leadership communications",
          "Mobile-friendly, accessible (WCAG AA)",
        ],
        microsoftStack:["SharePoint Online","Power Apps","Microsoft Viva","Azure AI Search","Microsoft Entra ID"],
        benefits:[
          {v:"4h",u:"/week",l:"Saved per staff member"},
          {v:"-55%",u:"",l:"Tier-1 IT/HR tickets"},
          {v:"92%",u:"",l:"Staff adoption rate"},
          {v:"WCAG AA",u:"",l:"Accessibility compliant"},
        ],
        outcomes:"NHS trust rolled out staff hub to 8,000 employees — saving ~32k hours/year on internal navigation and reducing Tier-1 helpdesk tickets by 55%.",
        relatedSolutions:["employee-self-service","sharepoint-modernisation","internal-business-portals"],
        process:[
          "Staff persona & service inventory workshops (Weeks 1–2)",
          "Information architecture & UX design (Weeks 3–4)",
          "Build portal & integrate backend systems (Weeks 5–9)",
          "Content migration & staff pilot (Weeks 10–11)",
          "Trust-wide launch & adoption campaign (Week 12)",
        ],
        faq:[
          {q:"Does this replace our existing HR/rota system?",a:"No — it sits on top. We integrate with your HR system (TRAC, Allocate, Workday) and rota system, presenting one consistent experience. The underlying systems remain unchanged."},
          {q:"Will it work on personal devices for staff who don't have NHS laptops?",a:"Yes — the portal is mobile-friendly and accessible via NHS-managed conditional access policies. Staff can access leave, payslips, and announcements from personal phones with MFA security."},
          {q:"How does AI search handle clinical policies?",a:"We index all policy documents in Azure AI Search with semantic embeddings. Clinicians ask 'what's the sepsis pathway for under-18s' and get the right policy section, version date, and direct link — cited and verifiable."},
        ],
      },
    ],
  },


  /* ════════════ 3. FINANCIAL SERVICES ════════════ */
  {
    slug:"financial-services", heading:"Financial Services", icon:"Dollar", color:C.blue, bg:C.blueL,
    desc:"Approvals, compliance & reporting",
    tagline:"Compliance-grade automation for banks, insurers & investment firms",
    overview:"Retail banks, commercial lenders, insurers, asset managers, and broker-dealers trust us to automate approval workflows, deliver SOC2/PCI-compliant compliance reporting, and modernise legacy core systems. We operate under your regulatory framework — FCA, PRA, SEC, FINRA, GDPR — with full audit trails.",
    industryStats:[
      {v:"25+",       l:"FS clients served"},
      {v:"£4B",       l:"AUM supported"},
      {v:"SOC2 + PCI", l:"Compliance certified"},
      {v:"99.99%",    l:"Production uptime SLA"},
    ],
    regulations:["SOC2 Type II", "PCI DSS", "FCA", "PRA", "SEC", "FINRA", "GDPR"],
    animVariant:"automation",
    animAccent:"#67e8f9",
    items:[
      {
        slug:"financial-approval-workflows", n:"CheckCircle", t:"Approval Workflows", tag:"Most Popular",
        d:"Multi-stage approvals with full audit",
        hero:"Replace email-based credit approvals, mortgage underwriting handoffs, and risk committee paper packs with policy-aware digital workflows. Multi-stage routing based on amount, risk score, and regulatory category — with full audit trails, SLA tracking, and one-click mobile approvals. Built for FCA, PRA, and SEC audit scrutiny.",
        challenges:[
          "Credit decisions stuck in email chains for days, hurting customer experience",
          "Audit trails reconstructed manually from emails when regulators ask",
          "Risk committee packs assembled from PDFs and Excel for hours each week",
          "Out-of-office bottlenecks cause SLA breaches and revenue leakage",
        ],
        approach:[
          { n:"Layers",    t:"Policy-driven routing",     d:"Multi-stage approval matrices: amount, risk, jurisdiction, product type — encoded in workflows, not in people's heads." },
          { n:"Shield",    t:"Regulatory audit trails",    d:"Every decision logged: who, when, why, what evidence — exportable for FCA/PRA/SEC audit in one click." },
          { n:"Users",     t:"Smart delegation",            d:"Out-of-office routing, role-based fallbacks, load balancing — no bottlenecks from a single approver's calendar." },
          { n:"Chart",     t:"SLA & exception dashboards", d:"Real-time view of approval queues, SLA breaches, exception patterns — for ops leaders and compliance heads." },
        ],
        features:[
          "Multi-stage sequential/parallel approval routing",
          "Policy-driven branching (amount, risk, jurisdiction)",
          "Mobile approvals from Outlook, Teams, app",
          "Out-of-office delegation & role-based fallbacks",
          "SLA tracking with auto-escalation",
          "Audit-grade decision logs with full context",
          "Regulatory report generation (audit packs)",
          "Integration with core banking, CRM, risk systems",
        ],
        microsoftStack:["Power Automate","Power Apps","Microsoft Teams","Outlook Actionable Messages","Dataverse"],
        benefits:[
          {v:"-72%",u:"",l:"Approval cycle time"},
          {v:"100%",u:"",l:"FCA audit-ready"},
          {v:"Zero",u:"",l:"Lost approvals"},
          {v:"1-click",u:"",l:"Mobile decisions"},
        ],
        outcomes:"Commercial bank cut credit-approval cycle time from 8 days to 18 hours across 12 approval types — reducing customer drop-off by 31% and improving NPS by 12 points.",
        relatedSolutions:["approval-automation","workflow-automation","ai-document-intelligence"],
        process:[
          "Approval matrix discovery & policy mapping (Weeks 1–2)",
          "Workflow design with compliance sign-off (Weeks 3–4)",
          "Build & integrate with core systems (Weeks 5–8)",
          "Pilot with one product line (Weeks 9–10)",
          "Phased rollout across product types (Weeks 11–14)",
        ],
        faq:[
          {q:"How do you handle complex underwriting approval matrices?",a:"Our most complex deployments handle 15+ approval stages with conditional branching on amount, customer segment, jurisdiction, product, and risk score. Every routing rule is configurable post-deployment so compliance and credit teams can adjust without dev cycles."},
          {q:"Will this meet FCA / PRA / SEC audit requirements?",a:"Yes — we design for regulatory audit from day one. Every approval is logged with timestamp, identity, evidence reviewed, comments, and prior state. Auditors receive complete decision histories, exportable to PDF or structured formats."},
          {q:"How does it integrate with our core banking system (Temenos, Mambu, FIS)?",a:"We integrate via APIs, message queues (Service Bus / MQ), or batch interfaces depending on the core. Approvals trigger downstream actions in the core; core events trigger workflow steps. We've worked with all major core platforms."},
        ],
      },
      {
        slug:"compliance-automation", n:"Shield", t:"Compliance Automation",
        d:"KYC, AML & regulatory automation",
        hero:"KYC checks, AML screening, sanctions monitoring, suspicious activity reporting — all running manually in spreadsheets and email is no longer defensible. We deploy compliance automation that handles screening, evidence capture, periodic reviews, and SAR/STR submissions — with full audit trails and automated regulatory reporting.",
        challenges:[
          "KYC onboarding takes 12+ days because checks are sequential and manual",
          "Sanctions screening missing matches due to inconsistent name handling",
          "Periodic reviews fall behind, triggering FCA findings at next inspection",
          "SAR/STR preparation is manual and prone to error",
        ],
        approach:[
          { n:"FileText",  t:"KYC & onboarding",          d:"Document capture, ID verification, sanctions screening, PEP checks — orchestrated in one workflow." },
          { n:"Shield",    t:"AML monitoring",             d:"Continuous transaction monitoring with ML-based anomaly detection — surfacing real risks, not false positives." },
          { n:"Brain",     t:"Periodic reviews",           d:"Risk-rated automatic review scheduling: low-risk every 3 years, high-risk annually — no clients forgotten." },
          { n:"Chart",     t:"Regulatory reporting",       d:"Automated SAR/STR generation, audit-ready evidence packs, regulator-format submissions." },
        ],
        features:[
          "KYC onboarding workflows with ID verification",
          "Sanctions, PEP, adverse media screening",
          "AML transaction monitoring with ML",
          "Periodic review scheduling and tracking",
          "SAR/STR preparation and submission",
          "Audit trails for FCA/FinCEN/MAS examiners",
          "Integration with screening providers (LexisNexis, Refinitiv)",
          "Compliance officer dashboard with case management",
        ],
        microsoftStack:["Power Automate","Azure AI Document Intelligence","Azure OpenAI","Dynamics 365","Power BI"],
        benefits:[
          {v:"-58%",u:"",l:"KYC onboarding time"},
          {v:"-83%",u:"",l:"False positive rate"},
          {v:"100%",u:"",l:"Periodic reviews on time"},
          {v:"Zero",u:"",l:"Missed regulatory filings"},
        ],
        outcomes:"Challenger bank reduced KYC onboarding from 14 days to 3 days while improving sanctions screening accuracy — passing FCA Section 166 review with no findings.",
        relatedSolutions:["ai-document-intelligence","approval-automation","ai-knowledge-assistants"],
        process:[
          "Compliance process audit & risk mapping (Weeks 1–3)",
          "KYC/AML workflow design (Weeks 4–5)",
          "Screening integration & ML model training (Weeks 6–10)",
          "Compliance team pilot (Weeks 11–12)",
          "Production rollout & regulator notification (Weeks 13–14)",
        ],
        faq:[
          {q:"Will this replace our existing AML / sanctions screening tool?",a:"Typically not — we integrate with established screening providers (LexisNexis WorldCompliance, Refinitiv World-Check, Dow Jones) which remain authoritative. We orchestrate the workflow, evidence capture, and case management around them."},
          {q:"How do you reduce false positives in transaction monitoring?",a:"We use Azure ML models trained on your historical alerts and outcomes — learning what constitutes 'true' vs 'false' positive in your context. Most clients see 70-85% false positive reduction within 6 months."},
          {q:"Can it handle multiple jurisdictions (UK, EU, US, APAC)?",a:"Yes — workflows have built-in jurisdiction routing. UK clients hit FCA rules, EU clients hit EBA/AMLD, US clients hit FinCEN, APAC clients hit local regulators. We deploy multi-jurisdiction setups regularly."},
        ],
      },
      {
        slug:"financial-reporting-analytics", n:"Chart", t:"Reporting & Analytics",
        d:"Regulatory & executive financial reporting",
        hero:"Regulatory reports (FINREP, COREP, MIFIR, Solvency II), board packs, NAV reporting, and management accounts — built on Power BI Premium and Microsoft Fabric. Pixel-perfect paginated outputs, scheduled distribution, audit-ready, and multi-entity/multi-currency. The reporting stack that scales from £100M to £100B AUM.",
        challenges:[
          "Regulatory reports assembled manually from 5+ systems, missing deadlines",
          "Board packs take 3 days to prepare every month with finance staff",
          "Multi-entity, multi-currency consolidations error-prone in Excel",
          "NAV reporting can't keep up with intra-day pricing demands",
        ],
        approach:[
          { n:"FileText",  t:"Paginated regulatory reports", d:"Pixel-perfect FINREP, COREP, MIFIR, Solvency II — directly to regulator format from Fabric data." },
          { n:"Chart",     t:"Executive dashboards",       d:"Live financial KPIs — revenue, EBITDA, AUM, NIMs — with AI commentary explaining variances." },
          { n:"Calc",      t:"Multi-entity consolidation", d:"Automated consolidation across entities, currencies, GAAPs — eliminations, FX, intercompany matching." },
          { n:"Zap",       t:"Scheduled distribution",     d:"Reports distributed via email, secure portal, Teams — to regulators, board, executives, on schedule." },
        ],
        features:[
          "Paginated regulatory reports (FINREP/COREP/MIFIR/Solvency II)",
          "Multi-entity, multi-currency consolidation",
          "Live executive dashboards with AI commentary",
          "Scheduled and ad-hoc report distribution",
          "Audit-trail & version control for every report",
          "Integration with core banking, fund admin, risk systems",
          "XBRL/iXBRL generation for regulators",
          "NAV reporting with intra-day refresh",
        ],
        microsoftStack:["Microsoft Fabric","Power BI Premium","Azure OpenAI","Azure Data Factory","Microsoft Purview"],
        benefits:[
          {v:"Same-day",u:"",l:"Regulatory submission"},
          {v:"-65%",u:"",l:"Board prep time"},
          {v:"Real-time",u:"",l:"Multi-entity P&L"},
          {v:"100%",u:"",l:"Audit-ready trails"},
        ],
        outcomes:"Asset manager cut monthly board-prep from 3 days to 4 hours while delivering same-day FINREP submissions — passing PRA Skilled Person review without findings.",
        relatedSolutions:["microsoft-fabric","executive-dashboards","enterprise-reporting"],
        process:[
          "Reporting inventory & rationalisation (Weeks 1–2)",
          "Data architecture & consolidation design (Weeks 3–5)",
          "Regulatory report build (Weeks 6–10)",
          "Board & executive dashboards build (Weeks 11–13)",
          "Distribution, scheduling & sign-off (Weeks 14–16)",
        ],
        faq:[
          {q:"How do you handle FINREP / COREP / Solvency II templates?",a:"We map your data to the EBA / EIOPA reporting templates and generate XBRL/iXBRL output ready for direct regulator submission. Templates are version-controlled and updated as regulations evolve."},
          {q:"Can it consolidate entities with different chart of accounts?",a:"Yes — we build account mapping layers per entity into a unified consolidated chart. FX, intercompany eliminations, minority interests — all handled at the semantic model layer in Fabric."},
          {q:"What about ad-hoc analysis on top of structured reports?",a:"Power BI Premium gives finance analysts self-service exploration on the same Fabric semantic models that power regulatory reports — so ad-hoc analysis ties exactly to what's been filed."},
        ],
      },
    ],
  },


  /* ════════════ 4. RETAIL & E-COMMERCE ════════════ */
  {
    slug:"retail-ecommerce", heading:"Retail & E-Commerce", icon:"Cart", color:C.orange, bg:C.orangeL,
    desc:"Operations, CX & retail analytics",
    tagline:"Unified commerce — from supply to shelf to customer experience",
    overview:"From fashion to grocery, from omnichannel chains to pure-play e-commerce, we modernise retail operations on Dynamics 365 Commerce and Power Platform. Store operations, clienteling, replenishment, customer 360, and retail analytics — all unified, all real-time, all built for omnichannel.",
    industryStats:[
      {v:"30+",     l:"Retailers served"},
      {v:"1.2k+",   l:"Stores supported"},
      {v:"Omnichannel", l:"Native architecture"},
      {v:"D365 Commerce", l:"Expert delivery"},
    ],
    regulations:["PCI DSS", "GDPR", "CCPA", "DSAR-ready"],
    animVariant:"apps",
    animAccent:"#a5b4fc",
    items:[
      {
        slug:"retail-operations-automation", n:"Zap", t:"Operations Automation", tag:"Most Popular",
        d:"Store ops, replenishment & merchandising",
        hero:"Store managers walk floors with clipboards; visual merchandising goes out by email; replenishment runs on Excel. We automate store operations end-to-end — daily checklists, planogram compliance, inventory replenishment, staff scheduling, and exception management — all on tablets, all real-time, all integrated with D365 Commerce.",
        challenges:[
          "Daily store checklists on paper, never visible to head office",
          "Planogram compliance verified by occasional store visits, not systematically",
          "Replenishment runs on Excel templates and store manager intuition",
          "Exception management (out-of-stocks, stockouts, mark-downs) is reactive and slow",
        ],
        approach:[
          { n:"Cpu",       t:"Mobile store ops apps",     d:"Daily checklists, planogram photos, inventory counts, mark-down management — all on store manager tablets." },
          { n:"Layers",    t:"Replenishment automation",   d:"Demand-driven replenishment using sales, weather, promotions — minimising stockouts and overstocks." },
          { n:"Chart",     t:"Compliance visibility",       d:"Head office sees real-time compliance — checklists, planograms, opening procedures — across all stores." },
          { n:"Brain",     t:"Demand & exception ML",       d:"ML-driven demand forecasting and exception detection — prioritising store manager attention." },
        ],
        features:[
          "Mobile store-ops app for managers (iOS/Android)",
          "Daily/weekly/monthly task automation",
          "Planogram compliance with photo verification",
          "Demand-driven inventory replenishment",
          "Mark-down and promotion management",
          "Staff scheduling with skill matching",
          "Real-time store-by-store performance dashboards",
          "Integration with D365 Commerce, POS, ERP",
        ],
        microsoftStack:["Dynamics 365 Commerce","Power Apps","Power Automate","Power BI","Azure Machine Learning"],
        benefits:[
          {v:"-23%",u:"",l:"Stockout rate"},
          {v:"+12%",u:"",l:"Planogram compliance"},
          {v:"2h",u:"/day",l:"Saved per manager"},
          {v:"Real-time",u:"",l:"HQ visibility"},
        ],
        outcomes:"Fashion chain (340 stores) cut stockout rates by 23% and saved 680 manager-hours/day across the estate — adding £4.8M in recovered revenue annually.",
        relatedSolutions:["workflow-automation","operations-management","enterprise-power-apps"],
        process:[
          "Store-ops process audit (Weeks 1–2)",
          "App design with store managers (Weeks 3–4)",
          "Build & integrate D365 Commerce, POS (Weeks 5–8)",
          "Pilot in 10 stores (Weeks 9–10)",
          "Estate-wide rollout (Weeks 11–14)",
        ],
        faq:[
          {q:"Will the mobile app work in stores with poor connectivity?",a:"Yes — Power Apps mobile clients work fully offline (basements, rural sites, in-mall dead zones). Checklists, photos, inventory counts work locally and sync automatically when connectivity returns."},
          {q:"Can it integrate with our existing POS (NCR, Toshiba, Verifone)?",a:"Yes — we integrate via APIs or middleware with all major POS systems. POS remains the transaction system of record; our solution overlays operations and management layers."},
          {q:"How does ML demand forecasting work for new stores or products?",a:"For new stores, we use similar-store baselines until 8–12 weeks of trading data accumulates. For new products, we use category-level patterns combined with launch attributes (price tier, season, promotion intensity)."},
        ],
      },
      {
        slug:"retail-customer-experience", n:"Headphones", t:"Customer Experience Apps",
        d:"Clienteling, loyalty & customer 360",
        hero:"Customer journeys today span web, mobile, store, social, and customer service — but most retailers have fragmented data and no single customer view. We build customer experience platforms on Dynamics 365 Customer Insights, Power Pages, and Azure AI — delivering true clienteling, omnichannel loyalty, and a real customer 360.",
        challenges:[
          "Customer data fragmented across web, mobile, POS, CRM, social",
          "Store associates can't see online history when customer walks in",
          "Loyalty programmes feel generic, not personalised",
          "Customer service operates in a separate silo from sales",
        ],
        approach:[
          { n:"Brain",     t:"True customer 360",          d:"Unify identity across web, mobile, store, social — single profile with full purchase, engagement, and service history." },
          { n:"Users",     t:"Clienteling apps",            d:"Store associates see full customer history on tablet — recommend based on actual buying patterns, not guesses." },
          { n:"Layers",    t:"Omnichannel loyalty",         d:"Loyalty rewards earned anywhere, redeemed anywhere — points, tiers, perks, personalised offers." },
          { n:"Headphones",t:"Connected service",           d:"Customer service sees the same 360 view — agents resolve faster, escalate intelligently." },
        ],
        features:[
          "Customer Data Platform on D365 Customer Insights",
          "Cross-channel identity resolution",
          "Mobile clienteling app for store associates",
          "Omnichannel loyalty engine",
          "Personalised email and SMS campaigns",
          "Customer service portal on Power Pages",
          "Customer journey analytics",
          "Integration with Shopify, Salesforce, e-commerce platforms",
        ],
        microsoftStack:["Dynamics 365 Customer Insights","Dynamics 365 Customer Service","Power Pages","Azure OpenAI","Dynamics 365 Marketing"],
        benefits:[
          {v:"+34%",u:"",l:"Repeat purchase rate"},
          {v:"+18 pt",u:"",l:"NPS improvement"},
          {v:"Single",u:" view",l:"Customer 360"},
          {v:"Real-time",u:"",l:"Personalisation"},
        ],
        outcomes:"Luxury retailer launched clienteling app to 800 associates — driving 34% lift in repeat purchases and £14M of identified clienteling-driven revenue in year one.",
        relatedSolutions:["customer-experience-apps","ai-knowledge-assistants","unified-data-platforms"],
        process:[
          "Customer journey mapping & data audit (Weeks 1–3)",
          "CDP architecture & identity resolution (Weeks 4–6)",
          "Clienteling app & loyalty engine build (Weeks 7–11)",
          "Customer service portal launch (Weeks 12–13)",
          "Cross-channel rollout & associate training (Weeks 14–16)",
        ],
        faq:[
          {q:"How does identity resolution work without violating GDPR?",a:"We use deterministic identity (email, loyalty number) plus probabilistic matching (device, household) with consent-aware controls. GDPR DSAR requests are handled through Customer Insights' built-in privacy tools."},
          {q:"Will this replace our existing loyalty platform?",a:"Often yes — Dynamics 365 with Customer Insights delivers equivalent loyalty capability with deeper integration. For specialised programmes (gambling, airline miles), dedicated platforms may stay; we integrate around them."},
          {q:"How do you measure ROI on clienteling apps?",a:"We baseline current associate-attributed revenue, deploy with a control group, and track lift in repeat rate, basket size, and clienteling-attributed sales. Most clients see 8–15% lift within 6 months."},
        ],
      },
      {
        slug:"retail-analytics", n:"Chart", t:"Retail Analytics",
        d:"Sell-through, customer & shrink analytics",
        hero:"Sell-through analysis, customer segmentation, shrink detection, basket analysis, promotion effectiveness — built on Microsoft Fabric with Power BI. Real-time dashboards for store managers, weekly insights for buyers, AI-generated commentary for executives. The retail data foundation that turns POS feeds into competitive advantage.",
        challenges:[
          "Sell-through analysis runs weekly, missing fast-moving fashion windows",
          "Customer segmentation built on assumptions, not behavioural data",
          "Shrink detection happens after stocktake reveals it's too late",
          "Promotion ROI argued about — never definitively measured",
        ],
        approach:[
          { n:"Layers",    t:"Unified retail data",        d:"POS, online, customer, inventory, supplier — unified on Microsoft Fabric with retail-specific semantic models." },
          { n:"Chart",     t:"Live operational analytics", d:"Real-time sell-through, store-by-store performance, inventory health — refreshed every 15 minutes." },
          { n:"Brain",     t:"AI-driven insights",          d:"GPT-4o generates 'what's working, what's not' commentary tailored to buyers, store managers, and executives." },
          { n:"Shield",    t:"Shrink & exception",          d:"ML-based shrink detection: identify unusual void patterns, return spikes, inventory anomalies — before stocktake." },
        ],
        features:[
          "Real-time sell-through dashboards (SKU/store/region)",
          "Customer segmentation with RFM, lifetime value, propensity",
          "Basket analysis and cross-sell intelligence",
          "Shrink detection with ML anomaly detection",
          "Promotion ROI measurement",
          "Buyer dashboards for assortment decisions",
          "Executive dashboards with AI commentary",
          "Integration with POS, e-commerce, CRM, inventory",
        ],
        microsoftStack:["Microsoft Fabric","Power BI Premium","Azure Machine Learning","Azure OpenAI","Dynamics 365 Commerce"],
        benefits:[
          {v:"Real-time",u:"",l:"Sell-through"},
          {v:"-19%",u:"",l:"Shrink reduction"},
          {v:"+8%",u:"",l:"Promotion ROI uplift"},
          {v:"AI",u:"-commentary",l:"What changed, why"},
        ],
        outcomes:"Grocery chain (220 stores) reduced inventory shrink by 19% and improved promotion ROI by 8% through ML-driven anomaly detection and AI-curated buyer insights.",
        relatedSolutions:["microsoft-fabric","executive-dashboards","predictive-insights"],
        process:[
          "Data estate audit (POS, online, inventory) (Weeks 1–3)",
          "Fabric setup & semantic models (Weeks 4–7)",
          "Operational dashboards build (Weeks 8–10)",
          "ML models (shrink, demand) (Weeks 11–13)",
          "Executive dashboards & rollout (Weeks 14–16)",
        ],
        faq:[
          {q:"Can it pull from multiple POS systems across our estate?",a:"Yes — we routinely consolidate data from 3-5 different POS systems (legacy stores, acquired chains, e-commerce platforms) into one unified Fabric data model. Source variety is the norm in retail."},
          {q:"How fast is real-time?",a:"For operational metrics (today's sales by store): 15-minute refresh. For e-commerce events: sub-minute via streaming. For financial reconciliation: end-of-day. We tune refresh rates to business needs vs cost."},
          {q:"What about specialist retail tools like Aptos or Oracle Retail?",a:"Specialist tools remain authoritative for their function — we integrate them. The data platform aggregates across them for unified analytics. Replace only what no longer earns its licence cost."},
        ],
      },
    ],
  },


  /* ════════════ 5. LOGISTICS & SUPPLY CHAIN ════════════ */
  {
    slug:"logistics-supply-chain", heading:"Logistics & Supply Chain", icon:"Truck", color:C.green, bg:C.greenL,
    desc:"Visibility, warehousing & vendors",
    tagline:"Visibility, control & automation across your supply chain",
    overview:"3PLs, manufacturers' supply chain divisions, distribution networks, and freight forwarders rely on us to deliver real-time supply-chain visibility, modern WMS deployments on Dynamics 365 SCM, and supplier management automation. Multi-modal, multi-country, multi-currency — built for global operations.",
    industryStats:[
      {v:"30+",       l:"Logistics clients"},
      {v:"200+",      l:"Warehouses managed"},
      {v:"99.5%",     l:"Pick accuracy achieved"},
      {v:"Multi-modal", l:"Road, rail, sea, air"},
    ],
    regulations:["ISO 28000", "C-TPAT", "AEO", "GDPR"],
    animVariant:"workplace",
    animAccent:"#7dd3fc",
    items:[
      {
        slug:"supply-chain-visibility", n:"Globe", t:"Supply Chain Visibility", tag:"Most Popular",
        d:"Real-time visibility across all nodes",
        hero:"Where is your inventory? Right now, across all DCs, in-transit, at suppliers, and in stores? Most supply chain leaders answer with caveats. We build real-time visibility platforms on Microsoft Fabric + Azure IoT — integrating ERP, WMS, TMS, supplier data, and IoT track-and-trace. One view, one truth, predictive alerts.",
        challenges:[
          "Inventory location data fragmented across ERP, WMS, supplier portals",
          "In-transit visibility limited to carrier emails and phone calls",
          "Disruption response reactive — we hear about problems from customers",
          "Demand spikes and supply gaps invisible until they hit revenue",
        ],
        approach:[
          { n:"Layers",    t:"Multi-system integration",   d:"ERP, WMS, TMS, supplier portals, IoT trackers — unified on Microsoft Fabric in real time." },
          { n:"Globe",     t:"End-to-end visibility",       d:"From supplier to customer: inventory locations, ETAs, exceptions, alternative routings — on one map." },
          { n:"Brain",     t:"Predictive disruption",       d:"ML detects late shipments, supplier delays, weather disruption — before they impact customer SLAs." },
          { n:"Chart",     t:"Executive scorecards",        d:"OTIF, perfect-order rate, inventory velocity, supplier scorecards — for supply chain leadership." },
        ],
        features:[
          "Multi-ERP / WMS / TMS integration",
          "Supplier and 3PL data ingestion",
          "IoT track-and-trace integration",
          "Live world map of inventory and shipments",
          "Predictive ETA and disruption alerts",
          "Exception management workflows",
          "OTIF and perfect-order scorecards",
          "Customer self-service shipment tracking portals",
        ],
        microsoftStack:["Microsoft Fabric","Azure IoT Hub","Dynamics 365 SCM","Power BI","Azure Machine Learning"],
        benefits:[
          {v:"Real-time",u:"",l:"E2E visibility"},
          {v:"+12pt",u:"",l:"OTIF improvement"},
          {v:"-31%",u:"",l:"Customer queries"},
          {v:"<5 min",u:"",l:"Disruption alert latency"},
        ],
        outcomes:"Global FMCG supply chain (47 DCs, 320 suppliers) achieved end-to-end visibility in 14 weeks — improving OTIF from 84% to 96% and reducing customer service tickets by 31%.",
        relatedSolutions:["microsoft-fabric","unified-data-platforms","predictive-insights"],
        process:[
          "Supply chain network mapping (Weeks 1–2)",
          "Integration architecture (ERP, WMS, suppliers) (Weeks 3–5)",
          "Data platform build & ingestion (Weeks 6–10)",
          "Visibility dashboards & predictive alerts (Weeks 11–13)",
          "Customer portal & rollout (Weeks 14–16)",
        ],
        faq:[
          {q:"How do you handle suppliers with no digital systems?",a:"For non-digital suppliers, we deploy Power Apps-based supplier portals (no licence needed for supplier users) or simple email-based status updates. About 95% of supplier visibility is achievable; the last 5% requires onboarding."},
          {q:"Can it integrate with carrier APIs (Maersk, DHL, FedEx, UPS)?",a:"Yes — we integrate with all major carrier APIs directly, plus aggregators (project44, Fourkites) when client already has them. We don't compete with those tools; we complement them."},
          {q:"What about multi-leg shipments with handoffs?",a:"Modeled as multi-leg journeys with handoff events. Each handoff updates inventory location, custody, and ETA. Predictive ML estimates onward leg delays based on actual transit time history."},
        ],
      },
      {
        slug:"warehouse-operations", n:"Package", t:"Warehouse Operations",
        d:"WMS, pick-pack & inventory ops",
        hero:"Modern warehouse operations on Dynamics 365 SCM and Warehouse Management — wave planning, mobile picking, slotting optimisation, robotic integration. Replace legacy WMS or paper-based picking with mobile-first, voice-enabled, robotically-augmented operations. From 99.5% pick accuracy to 30% labour productivity gains.",
        challenges:[
          "Legacy WMS expensive to maintain and slow to evolve",
          "Pick accuracy below 98% causes customer complaints and chargebacks",
          "Manual slotting means high-velocity SKUs in low-traffic locations",
          "Labour productivity flat despite growing volumes",
        ],
        approach:[
          { n:"Cpu",       t:"Mobile-first picking",       d:"Hand-held devices with voice and barcode — guided picking paths, 99.5%+ accuracy." },
          { n:"Brain",     t:"ML-driven slotting",          d:"Velocity-based slotting using actual pick paths — reducing travel distance by 30-40%." },
          { n:"Layers",    t:"Robotic integration",         d:"AMR/AGV integration with D365 — robotic moves orchestrated alongside human picks." },
          { n:"Chart",     t:"Labour productivity",         d:"Live productivity dashboards by associate, shift, zone — incentive-aligned management." },
        ],
        features:[
          "D365 Warehouse Management implementation",
          "Mobile picking apps (Android, voice, scan)",
          "Wave planning and release management",
          "ML-driven slotting optimisation",
          "AMR/AGV robotic integration",
          "Cycle counting automation",
          "Inbound and outbound dock scheduling",
          "Live labour productivity dashboards",
        ],
        microsoftStack:["Dynamics 365 Warehouse Management","Power Apps","Power Automate","Azure IoT Hub","Power BI"],
        benefits:[
          {v:"99.5%",u:"",l:"Pick accuracy"},
          {v:"+28%",u:"",l:"Labour productivity"},
          {v:"-35%",u:"",l:"Travel distance"},
          {v:"Robotic",u:"",l:"AMR-integrated"},
        ],
        outcomes:"3PL operator deployed D365 Warehouse Management across 18 DCs — achieving 99.7% pick accuracy and 28% labour productivity improvement, saving £4.2M in annual operating costs.",
        relatedSolutions:["dynamics-365-solutions","operations-management","enterprise-power-apps"],
        process:[
          "Warehouse process audit & priority workshop (Weeks 1–3)",
          "WMS architecture & integration design (Weeks 4–5)",
          "Configure D365 WM & build mobile apps (Weeks 6–11)",
          "Pilot at one warehouse (Weeks 12–14)",
          "Multi-site rollout (Weeks 15–22)",
        ],
        faq:[
          {q:"How does D365 Warehouse Management compare to Manhattan or SAP EWM?",a:"For Microsoft-centric organisations, D365 WM delivers comparable functionality at 50-70% lower TCO. For multi-billion turnover operations or extremely complex distribution, Manhattan/SAP retain advantages. We'll honestly recommend the right fit."},
          {q:"Can you integrate AMRs / AGVs (Geek+, Locus, AutoStore)?",a:"Yes — we integrate with all major robotics platforms via their APIs. AMRs handle long-distance moves while humans pick at stations, orchestrated by D365 WM as the unified controller."},
          {q:"What about ASRS (Automated Storage / Retrieval) integration?",a:"Yes — ASRS systems (Dematic, Kardex, Vanderlande) integrate via their orchestration layers. We've deployed alongside ASRS at clients with 100k+ daily order volumes."},
        ],
      },
      {
        slug:"vendor-management-automation", n:"Users", t:"Vendor Management Automation",
        d:"Supplier onboarding & SRM",
        hero:"Supplier onboarding takes weeks because compliance, finance, procurement, and legal each have their own checks. Performance reviews happen quarterly because data is spread across systems. We deploy supplier relationship management (SRM) platforms that automate onboarding, monitor performance continuously, and turn vendor management strategic.",
        challenges:[
          "Supplier onboarding takes 6+ weeks due to fragmented checks",
          "Vendor performance reviewed quarterly with stale, manual data",
          "Risk exposure (sanctions, financial health, ESG) reviewed once per year",
          "Contract renewals and SLA breaches caught reactively",
        ],
        approach:[
          { n:"Users",     t:"Self-service onboarding",   d:"Supplier portal for documents, compliance, banking, ESG declarations — orchestrated through internal approvals." },
          { n:"Shield",    t:"Continuous due diligence",   d:"Automated sanctions screening, financial health monitoring, ESG scoring, news monitoring — alerts on changes." },
          { n:"Chart",     t:"Performance scorecards",     d:"Real-time delivery, quality, cost, responsiveness scores — across all suppliers, refreshed daily." },
          { n:"FileText",  t:"Contract intelligence",      d:"Contract repository with auto-extracted obligations, SLAs, renewal dates — proactive alerts before breaches." },
        ],
        features:[
          "Supplier self-service onboarding portal",
          "Automated due-diligence workflows",
          "Sanctions and PEP screening integration",
          "Financial health and ESG monitoring",
          "Real-time performance scorecards",
          "Contract repository with AI extraction",
          "Renewal and SLA breach alerts",
          "Supplier risk dashboards for procurement leaders",
        ],
        microsoftStack:["Power Apps","Power Pages","Power Automate","Azure AI Document Intelligence","Dynamics 365"],
        benefits:[
          {v:"-72%",u:"",l:"Onboarding time"},
          {v:"Real-time",u:"",l:"Performance data"},
          {v:"100%",u:"",l:"Contract visibility"},
          {v:"Continuous",u:"",l:"Risk monitoring"},
        ],
        outcomes:"Logistics group reduced supplier onboarding from 32 to 9 days across 1,400 suppliers — and identified £840k of off-contract spend in the first quarter post go-live.",
        relatedSolutions:["procurement-automation","ai-document-intelligence","approval-automation"],
        process:[
          "Vendor management process audit (Weeks 1–2)",
          "Supplier portal & onboarding design (Weeks 3–4)",
          "Build portal & integrate D365 / ERP (Weeks 5–9)",
          "Migrate existing suppliers & supplier onboarding (Weeks 10–12)",
          "Performance scorecards & risk monitoring (Weeks 13–14)",
        ],
        faq:[
          {q:"Will suppliers really use a portal vs email?",a:"Once suppliers understand the portal is the only path to PO and payment, adoption is universal within 60-90 days. We provide multi-language support and email-fallback for very small suppliers."},
          {q:"How do you handle ESG / Modern Slavery / sanctions compliance?",a:"We integrate with established compliance providers (Sedex, EcoVadis, LexisNexis, Refinitiv) — keeping their data authoritative while orchestrating workflows, alerting on changes, and aggregating into your supplier risk view."},
          {q:"Can it work alongside our existing P2P platform (Coupa, Ariba)?",a:"Yes — we typically complement P2P platforms by adding deeper SRM capabilities (onboarding, performance, risk) that P2P tools don't address well. Some clients eventually consolidate; many keep both."},
        ],
      },
    ],
  },


  /* ════════════ 6. PROFESSIONAL SERVICES ════════════ */
  {
    slug:"professional-services", heading:"Professional Services", icon:"Brief", color:C.purple, bg:C.purpleL,
    desc:"Employee ops, knowledge & process",
    tagline:"Operational excellence for consultancy, legal, accounting & advisory firms",
    overview:"Consulting firms, law firms, accounting practices, and advisory businesses face the same challenges: ramping new joiners, organising tacit knowledge, automating internal processes, and managing client engagements at scale. We modernise these workflows on Microsoft 365 — turning knowledge into competitive advantage.",
    industryStats:[
      {v:"40+",       l:"Professional firms served"},
      {v:"22k+",      l:"Knowledge workers supported"},
      {v:"GDPR",      l:"Privilege-compliant"},
      {v:"D365 Project Ops", l:"Expert delivery"},
    ],
    regulations:["GDPR", "ISO 27001", "SRA (Solicitors)", "ICAEW (Accountants)"],
    animVariant:"ai",
    animAccent:"#67e8f9",
    items:[
      {
        slug:"ps-employee-operations", n:"Users", t:"Employee Operations", tag:"Most Popular",
        d:"Joiner-mover-leaver, HR & internal services",
        hero:"Professional firms hire heavily, lose people, and need every new joiner productive on day one. We automate the full employee lifecycle — from offer to onboarding to mid-career moves to clean offboarding — across HR, IT, finance, facilities, and learning. Built on Power Platform with HRIS integration, GDPR-compliant, mobile-first.",
        challenges:[
          "New joiners take 3+ weeks to be productive due to manual onboarding",
          "IT provisioning, training assignments, system access happen in parallel — chaotically",
          "Mid-career moves (promotions, role changes) trigger another wave of manual updates",
          "Offboarding leaves dormant access for weeks — security exposure",
        ],
        approach:[
          { n:"Users",     t:"Day-1 onboarding",          d:"From offer-accepted: kit ordering, IT provisioning, security training, manager intro, system access — all automated." },
          { n:"Layers",    t:"Joiner-mover-leaver",        d:"HRIS-driven workflows for every employee change — provisioning, access, training, payroll updates." },
          { n:"Shield",    t:"Access governance",          d:"Automatic access revocation on leave, role changes, contract end — eliminating dormant access risk." },
          { n:"Chart",     t:"HR dashboards",              d:"Time-to-productive, attrition, training compliance, internal mobility — for HRBPs and partners." },
        ],
        features:[
          "Pre-boarding kit and welcome packet automation",
          "Day-1 IT provisioning (Entra ID, M365, practice management)",
          "Self-service leave with manager approval workflows",
          "Performance review cycle management",
          "Training & certification tracking",
          "Mid-career move automation (promotions, transfers)",
          "Offboarding checklists with access revocation",
          "Integration with major HRIS (Workday, SuccessFactors, HiBob)",
        ],
        microsoftStack:["Power Automate","Power Apps","SharePoint Online","Microsoft Entra ID","Microsoft Teams"],
        benefits:[
          {v:"Day-1",u:"",l:"Productive new hires"},
          {v:"-68%",u:"",l:"HR admin time"},
          {v:"Zero",u:"",l:"Dormant access"},
          {v:"GDPR",u:"",l:"Compliant by design"},
        ],
        outcomes:"Mid-tier consultancy (450 hires/year) cut new-hire admin from 8 hours to 45 minutes per hire — saving 3,375 HR hours/year and improving 90-day attrition by 18%.",
        relatedSolutions:["hr-process-automation","workflow-automation","collaboration-automation"],
        process:[
          "HR process discovery & priority workshop (Weeks 1–2)",
          "Workflow design with HRBP sign-off (Weeks 3–4)",
          "HRIS integration & build (Weeks 5–7)",
          "Pilot with one practice area (Weeks 8–9)",
          "Firm-wide rollout & training (Weeks 10–12)",
        ],
        faq:[
          {q:"Will this replace our HRIS (Workday, SuccessFactors, HiBob)?",a:"No — we integrate with your HRIS as the system of record. The automation handles the orchestration, notifications, and cross-system tasks that HRIS systems traditionally don't address well."},
          {q:"Can we customise for different practice areas / countries?",a:"Yes — workflows have built-in branching for practice area, role family, country (different regulations), and seniority. Most professional services clients run 15-25 variant flows within a single platform."},
          {q:"How tightly does it integrate with practice management systems?",a:"We integrate with Aderant, Elite 3E, Iris Star, Mosaic and others via APIs. Joiner-mover-leaver events trigger updates in practice management, finance, and time-tracking systems automatically."},
        ],
      },
      {
        slug:"ps-knowledge-systems", n:"BookOpen", t:"Internal Knowledge Systems",
        d:"Capture, find & reuse firm knowledge",
        hero:"Professional firms' single greatest asset is their collective knowledge. Yet most of it lives in heads, lost emails, and unfindable Word documents. We deploy AI-powered knowledge platforms — Viva Topics, Azure AI Search, custom Copilots — that capture, organise, and surface knowledge across the firm. New joiners ramp 3× faster. Senior expertise becomes scalable.",
        challenges:[
          "Tacit knowledge walks out the door when partners retire",
          "New joiners take months to find precedents, templates, methodologies",
          "Same questions answered repeatedly by senior staff",
          "Pitch documents, case studies, prior work — unfindable when needed",
        ],
        approach:[
          { n:"Brain",     t:"AI knowledge assistant",     d:"Custom Copilot grounded in firm-wide content — answering 'what's our position on X' in seconds with citations." },
          { n:"BookOpen",  t:"Topic auto-discovery",        d:"Viva Topics scans SharePoint, Teams, OneDrive to auto-identify topics, experts, and key documents." },
          { n:"Users",     t:"Expert directory",            d:"Find the right internal expert by topic, with recent work and availability — replacing 'does anyone know X?' emails." },
          { n:"Shield",    t:"Privilege & confidentiality", d:"Permission-aware search respecting matter privilege, client confidentiality, ethical walls." },
        ],
        features:[
          "Custom AI assistant for firm-wide knowledge queries",
          "Viva Topics auto-discovery deployment",
          "Expert directory with availability and recent work",
          "Curated topic pages, FAQs, methodologies",
          "Precedent and template management",
          "Pitch and credentials library with AI search",
          "Ethical walls and client conflict management",
          "Knowledge contribution incentive workflows",
        ],
        microsoftStack:["Microsoft Viva Topics","Azure AI Search","Microsoft Copilot Studio","SharePoint Online","Azure OpenAI"],
        benefits:[
          {v:"3×",u:"",l:"Faster new-hire ramp"},
          {v:"-58%",u:"",l:"Time finding precedents"},
          {v:"AI",u:"-cited",l:"Verifiable answers"},
          {v:"Privilege",u:"",l:"-aware search"},
        ],
        outcomes:"Top-50 law firm cut precedent-search time from 38 to 9 minutes — recovering ~£3.4M of fee-earner time annually and improving junior associate satisfaction scores by 22%.",
        relatedSolutions:["ai-knowledge-assistants","knowledge-management","enterprise-copilots"],
        process:[
          "Knowledge audit & topic strategy (Weeks 1–3)",
          "Viva Topics deployment & initial discovery (Weeks 4–5)",
          "Topic curation & expert directory (Weeks 6–8)",
          "AI assistant build & grounding (Weeks 9–11)",
          "Firm-wide rollout & adoption (Weeks 12–14)",
        ],
        faq:[
          {q:"How does this protect client confidentiality and matter privilege?",a:"Permission-aware search trims results based on user's matter team membership and ethical wall configuration. Privileged content only surfaces to authorised users. This is non-negotiable; we design for it from day one."},
          {q:"Will partners actually contribute knowledge?",a:"Most professionals contribute when (a) it's frictionless (Viva Topics auto-discovers from existing work) and (b) there's tangible benefit (their expertise becomes discoverable, increases internal influence). We work with KM leads on incentive design."},
          {q:"What about content in case management systems (Filevine, NetDocuments)?",a:"We integrate with major matter/document management systems via APIs — ingesting metadata and authoring context while honouring source-system access controls."},
        ],
      },
      {
        slug:"ps-process-automation", n:"Zap", t:"Business Process Automation",
        d:"Internal workflows, time tracking & engagement ops",
        hero:"Time recording, engagement intake, conflict checks, billing approvals, expense management, partner approvals — every back-office process in professional services has compliance demands and audit needs. We automate the lot on Power Platform, integrated with practice management systems, with full audit trails.",
        challenges:[
          "Time recording happens days after the fact, accuracy poor",
          "Engagement intake (conflict, KYC, fee approval) takes 2+ weeks",
          "Expense approvals stuck in email chains, blocking partner reviews",
          "Audit-trail gaps cause SRA / ICAEW findings",
        ],
        approach:[
          { n:"Zap",       t:"Internal workflows",         d:"Time recording prompts, engagement intake, conflict checks, billing approvals — automated end-to-end." },
          { n:"Layers",    t:"Practice management integration", d:"Bridge Power Platform to Aderant, Elite 3E, Iris, Mosaic — single source of truth, multiple workflows on top." },
          { n:"Shield",    t:"Audit & compliance",          d:"Every action timestamped, attributed, evidenced — passing SRA, ICAEW, ISO 27001 audits." },
          { n:"Chart",     t:"Utilisation dashboards",       d:"Real-time chargeable hours, realisation rates, leverage ratios — for partners and practice leaders." },
        ],
        features:[
          "Time recording prompts in Teams, Outlook, mobile",
          "Engagement intake workflows (KYC, conflict, fee letter)",
          "Conflict checking automation",
          "Expense management with policy enforcement",
          "Billing approval and write-off workflows",
          "Partner approval chains",
          "Audit trails for SRA / ICAEW compliance",
          "Real-time utilisation dashboards",
        ],
        microsoftStack:["Power Automate","Power Apps","Microsoft Teams","Dataverse","Power BI"],
        benefits:[
          {v:"+18%",u:"",l:"Time recording accuracy"},
          {v:"-65%",u:"",l:"Engagement intake time"},
          {v:"100%",u:"",l:"Audit-trail coverage"},
          {v:"Live",u:"",l:"Utilisation visibility"},
        ],
        outcomes:"Big-4 advisory practice cut engagement intake from 14 to 4 days while improving time-recording accuracy by 18% — adding 7,200 chargeable hours annually across the practice.",
        relatedSolutions:["workflow-automation","approval-automation","finance-automation"],
        process:[
          "Process audit & priority workshop (Weeks 1–2)",
          "Workflow design & PMS integration plan (Weeks 3–4)",
          "Build & integrate practice management (Weeks 5–9)",
          "Pilot with one practice area (Weeks 10–11)",
          "Firm-wide rollout & training (Weeks 12–14)",
        ],
        faq:[
          {q:"Will this disrupt our existing practice management system?",a:"No — your practice management (Aderant, Elite 3E, Iris, Mosaic) remains the system of record for matters, time, and billing. We orchestrate workflows around it, surfacing approvals and prompts where fee-earners actually work (Teams, Outlook)."},
          {q:"How does it work with our existing time-recording tool (iTimekeep, Intapp)?",a:"We layer prompts and intelligence over existing time-recording tools rather than replacing them. Power Automate triggers reminders, AI-suggested narratives, and approval workflows on top of the canonical time-recording system."},
          {q:"Can it handle multi-jurisdiction engagement intake (US, UK, EU)?",a:"Yes — intake workflows branch by jurisdiction for AML, conflict, fee letter, and ethical wall configuration. We've deployed multi-country setups handling 12+ jurisdictions with localised compliance flows."},
        ],
      },
    ],
  },

]
